import { Button, ButtonVariation } from '../../../components/Button/Button';
import type { RosterChange } from './UpdateRosterModal';
import './UnsavedRosterChangesModal.css';

interface UnsavedRosterChangesModalProps {
  /** The scheduled changes that have not been sent yet. */
  changes: RosterChange[];
  /** Save the staged changes, then continue to wherever the user was heading. */
  onCertify: () => void;
  /** Abandon the changes and continue to wherever the user was heading. */
  onLeave: () => void;
  /** Stay on the roster with the staged changes intact. */
  onCancel: () => void;
}

/**
 * ACP-2183: scheduling a removal only stages it — nothing reaches the contract until
 * Update Roster is clicked. Leaving the page before that threw the work away without a
 * word, which is what INC1980606, INC1980267 and INC1979005 all describe.
 *
 * The staged changes are listed rather than summarised. "You have unsaved changes" asks
 * the user to remember what they did; naming the people they scheduled lets them decide
 * whether it is worth going back for.
 *
 * Wording and the three-button layout come from HCD (Rebecca Gallagher, 2026-10-21),
 * building on Rachel Wingender's mockup. Certify Roster saves from here rather than
 * sending the user back to find the button, which is the whole reason they were warned.
 */
function UnsavedRosterChangesModal({ changes, onCancel, onCertify, onLeave }: UnsavedRosterChangesModalProps) {
  const baseTestId = 'unsaved-roster-changes-modal';

  return (
    <div
      className='unsaved-roster-changes-modal'
      data-testid={baseTestId}>
      <p data-testid={`${baseTestId}-text`}>
        You are about to leave this page with the following unsaved changes. Click &quot;Certify Roster&quot; if you
        want to save your changes.
      </p>

      <ul
        className='unsaved-roster-changes-modal__list'
        data-testid={`${baseTestId}-changes`}>
        {changes.map((change, index) => (
          <li key={`unsaved-${index}`}>
            {change.userName} scheduled for removal on {change.date}.
          </li>
        ))}
      </ul>

      <div
        className='unsaved-roster-changes-modal__actions'
        data-testid={`${baseTestId}-actions`}>
        <Button
          bordered
          isAutoWidth
          data-testid={`${baseTestId}-cancel`}
          type='button'
          variation={ButtonVariation.GHOST}
          onClick={onCancel}>
          Cancel
        </Button>
        <Button
          isAutoWidth
          data-testid={`${baseTestId}-leave`}
          type='button'
          variation={ButtonVariation.SOLID}
          onClick={onLeave}>
          Leave and Discard
        </Button>
        <Button
          isAutoWidth
          data-testid={`${baseTestId}-certify`}
          type='button'
          variation={ButtonVariation.SOLID}
          onClick={onCertify}>
          Certify Roster
        </Button>
      </div>
    </div>
  );
}

export default UnsavedRosterChangesModal;
